import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import type { CampaignOverviewResponse, CampaignResponse } from 'clients/CampaignsClient/CampaignsClient.types';
import type { CandidateStatus } from 'services/candidates';
import type { ActionableCampaign, CampaignManagerFilters } from './CampaignManagerContext.types';

import { CampaignOverviewStatus } from 'clients/CampaignsClient/CampaignOverviewStatus.types';
import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { queryClient } from 'config/queryClient';
import { routes } from 'config/routes';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { QueryKey } from 'types/query';
import { useDialog } from '../DialogContext/hooks/useDialog';
import { LocalizationContext } from '../LocalizationContext/LocalizationContext';
import { setCampaign } from './CampaignManagerContext.useCampaign';
import { useCampaigns } from './CampaignManagerContext.useCampaigns';

export const useInjectCampaignActions = ({
  filters: { status, teamview, name },
  selectedCampaignOverviewId,
  onSelectCampaignOverviewId,
}: {
  filters: CampaignManagerFilters;
  selectedCampaignOverviewId: number | undefined;
  onSelectCampaignOverviewId: (id: number | undefined) => void;
}) => {
  const { removeCampaignFromOverview: _removeCampaignFromOverview } = useCampaigns({ status, teamview, name });
  const candidateOutreachModal = useGlobalDialog(GlobalDialogType.CandidateOutreach);
  const { dictionary } = useContext(LocalizationContext);
  const dialog = useDialog();
  const navigate = useNavigate();

  const removeCampaignFromOverview = useCallback(
    async (id: number) => {
      return _removeCampaignFromOverview(id).then((data) => {
        const isSelected = id === selectedCampaignOverviewId;

        if (!isSelected || !data?.pages) return;

        const [firstAvailableCampaign] = data.pages.flatMap((campaign) => campaign.results);

        if (!firstAvailableCampaign) {
          onSelectCampaignOverviewId(undefined);
          navigate(routes.search({ campaignId: '' }));

          return;
        }

        onSelectCampaignOverviewId(firstAvailableCampaign.id);
        navigate(routes.search({ campaignId: firstAvailableCampaign.id }));
      });
    },
    [selectedCampaignOverviewId, _removeCampaignFromOverview],
  );

  const handleSelect = (id: number, initialTab?: CandidateStatus) => {
    onSelectCampaignOverviewId(id);
    queryClient.removeQueries([QueryKey.campaignManagerContextUseCampaign, id]);

    candidateOutreachModal.close();

    navigate(routes.search({ campaignId: id, tab: initialTab }));
  };

  const handleDuplicate = (id: number) => {
    return new Promise<void>((res, rej) =>
      campaignsClient
        .copyCampaign(id)
        .then(async ({ data: { id: duplicatedCampaignId } }) => {
          onSelectCampaignOverviewId(duplicatedCampaignId);
          await queryClient.invalidateQueries([QueryKey.campaignManagerContextUseCampaigns]);

          res();
        })
        .catch(rej),
    );
  };

  const handleChangeOnwer = (campaignId: number, userId: number) => {
    return new Promise<void>((res, rej) => {
      campaignsClient
        .changeOwner({ campaignId, userId })
        .then(async () => {
          await queryClient.invalidateQueries([QueryKey.campaignManagerContextUseCampaigns]);
          res();
        })
        .catch(rej);
    });
  };

  const handleToggleStatus = useCallback(
    (id: number, oldStatus: CampaignOverviewStatus) => {
      const status =
        oldStatus === CampaignOverviewStatus.active ? CampaignOverviewStatus.inactive : CampaignOverviewStatus.active;

      return new Promise<void>((res, rej) => {
        campaignsClient
          .updateCampaign(id, { status })
          .then(() => removeCampaignFromOverview(id).finally(res))
          .catch(rej);
      });
    },
    [removeCampaignFromOverview],
  );

  const handleDelete = useCallback(
    (id: number) => {
      return new Promise<void>((res, rej) => {
        dialog.open({
          title: dictionary.areYouSure,
          description: dictionary.confirmDeleteCampaign,
          confirmAction: {
            label: dictionary.delete,
            onClick: () => {
              campaignsClient
                .deleteCampaign(id)
                .then(() => {
                  removeCampaignFromOverview(id).finally(res);
                })
                .catch(rej);
            },
          },
          cancelAction: {
            label: dictionary.cancel,
            onClick: () => {
              res();
            },
          },
        });
      });
    },
    [dictionary, removeCampaignFromOverview],
  );

  const handleUpdateName = useCallback((id: number, name: string) => {
    return new Promise<void>((res, rej) => {
      campaignsClient
        .updateCampaign(id, { name })
        .then(() => {
          setCampaign(id, (campaign) => ({ ...campaign, name }));
          res();
        })
        .catch(rej);
    });
  }, []);

  const injectCampaignActions = useCallback(
    (campaign: CampaignOverviewResponse | CampaignResponse): ActionableCampaign => {
      return {
        select: (initialTab?: CandidateStatus) => handleSelect(campaign.id, initialTab),
        duplicate: () => handleDuplicate(campaign.id),
        toggleStatus: () => handleToggleStatus(campaign.id, status),
        delete: () => handleDelete(campaign.id),
        changeOwner: (campaignId: number, userId: number) => handleChangeOnwer(campaignId, userId),
        updateName: (name) => handleUpdateName(campaign.id, name),
      };
    },
    [status, teamview, handleDelete, handleToggleStatus],
  );

  return injectCampaignActions;
};
